import { useCallback, type VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../../contexts/ToastContext';
import Redirect from '../../../../../generic/Redirect';
import { shouldDisplayOrganizationPortalSettings } from '../../../../../settings/shared/permissions';
import ProjectGroupTokenSettings from '../../../components/ProjectGroupPortalSettings';
import { commitTokenChanges } from '../../../request';
import usePortalSettingsState from '../../hooks/usePortalSettingsState';
import useUpdateReleaseGroup from '../../hooks/useUpdateReleaseGroup';
import { useReleaseGroupsSettings } from '../context';
import type { RouterProps } from '../../../../../../types';

type Props = RouterProps<{ projectGroupId: string }>;

/**
 * ReleaseGroupsSettings is the container for the release groups portal settings pages.
 */
const ReleaseGroupsSettingsPortal: VoidFunctionComponent<Props> = ({ params }) => {
  const projectGroupId = Number(params.projectGroupId); // This needs to be cast to a number, so that it can be used in the hooks
  //  We only need this to tell us what the release group's current publicOnPortal setting is
  const { data, onRefetchAfterSave } = useReleaseGroupsSettings();
  const onUpdateReleaseGroup = useUpdateReleaseGroup();
  const { state, actions } = usePortalSettingsState(data.publicOnPortal);
  const onAddToast = useAddToast();

  const isDirty = data.publicOnPortal !== state.publicOnPortal
    || state.pendingAddTokenIds.length > 0
    || state.pendingRemoveTokenIds.length > 0;

  const onSave = useCallback(async () => {
    if (isDirty) {
      //  If specifically the publicOnPortal setting has changed, we need to update the project group
      if (data.publicOnPortal !== state.publicOnPortal) {
        await onUpdateReleaseGroup(projectGroupId, {
          publicOnPortal: state.publicOnPortal,
        });
      }
      //  Separate request to update the actual tokens
      if (state.pendingAddTokenIds.length > 0 || state.pendingRemoveTokenIds.length > 0) {
        await commitTokenChanges(
          projectGroupId,
          state.pendingAddTokenIds,
          state.pendingRemoveTokenIds,
        );
      }

      //  We need to update Redux, so this will trigger a refetch of the project group
      //  which passes down the settings to this component. This isn't the most efficient,
      //  but it's the easiest way to keep the settings in sync.
      await onRefetchAfterSave();

      onAddToast({ type: 'success', content: 'Portal settings saved' });
      actions.onReset(); // Reset the local state
    }
  }, [
    isDirty,
    data.publicOnPortal,
    state,
    actions,
    onRefetchAfterSave,
    onAddToast,
    onUpdateReleaseGroup,
    projectGroupId,
  ]);

  //  This guards against the rare case where a Viewer user guesses the URL
  if (!shouldDisplayOrganizationPortalSettings()) {
    return (
      <Redirect to={`/projects/group/${projectGroupId}/settings/general`} />
    );
  }

  return (
    <ProjectGroupTokenSettings
      projectGroupId={projectGroupId}
      onAddToken={actions.onAddToken}
      onRemoveToken={actions.onRemoveToken}
      publicOnPortal={state.publicOnPortal}
      setPublicOnPortal={actions.setPublicOnPortal}
      isDirty={isDirty}
      onSave={onSave}
      isOnFullPage
    />
  );
};

export default ReleaseGroupsSettingsPortal;
